import React from 'react';

import { useTranslation } from 'react-i18next';
import { Table, PageSizeType, Sort } from 'design-system-web';
import {
  useGetProductsServicesTableColumns,
  useGetProductsServicesColumns,
} from '../helpers';
import { Product } from 'lane-shared/domains/billingPayments/types';
import { deleteProductMutation } from 'lane-shared/domains/billingPayments/queries/deleteProductMutation';
import { useProductsServicesTableFilters } from './hooks/useProductsServicesTableFilters';
import { useProductServiceTable } from './hooks/useProductServiceTable';
import { exportCSV } from '../../charges-invoices/helpers/exportCSV';

import { TableFilter } from './types';

type Props = {
  canManage?: boolean;
};

export function ProductsServicesTable({ canManage }: Props) {
  const { t } = useTranslation();

  const { filters: tableFilters } = useProductsServicesTableFilters();
  const {
    channel,
    page,
    products,
    refetchProducts,
    getBulkProductsAndServices,
    setPage,
    setFilters,
    setSearchKey,
    setSortBy,
  } = useProductServiceTable();

  const csvTableFields = useGetProductsServicesColumns(channel?.id);

  function handleSortChange(sort: Sort) {
    setSortBy({
      id: sort.id,
      direction: sort.direction,
    });
  }

  function handlePageSizeChange(updatedPageSize: PageSizeType) {
    setPage({ ...page, size: updatedPageSize });
  }

  function handleSetPage(pageIndex: number) {
    if (pageIndex !== page.index) {
      setPage({ ...page, index: pageIndex });
    }
  }

  function handleProductNameSearch(searchKey: string) {
    setSearchKey(searchKey);
  }

  function handleFiltersChange(filters: any[]) {
    const f: TableFilter[] = filters;

    setFilters(f);
  }

  const onDelete = async (product: Product) => {
    try {
      await window.Alert.confirm({
        title: t('abp.productsServices.deleteProduct.title', {
          product: product.name,
        }),
        message: t('abp.productsServices.deleteProduct.description'),
        confirmText: t('abp.productsServices.deleteProduct.action.delete'),
        cancelText: t('abp.productsServices.deleteProduct.action.cancel'),
        confirmButtonStyle: {
          backgroundColor: '#C7200A',
          borderColor: '#C7200A',
          color: 'white',
        },
      });
    } catch {
      // user cancelled
      return;
    }

    try {
      if (channel?.id) {
        await deleteProductMutation(product.id, channel.id);

        window.Toast.show(
          t('abp.productsServices.deleteProduct.delete.success')
        );

        await refetchProducts();
      }
    } catch (error) {
      let errorMessage = t('abp.productsServices.deleteProduct.delete.error');

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      try {
        await window.Alert.confirm({
          title: t('abp.productsServices.deleteProduct.errorTitle', {
            product: product.name,
          }),
          message: errorMessage,
          confirmText: t('abp.productsServices.deleteProduct.gotIt'),
          cancelText: '',
          confirmButtonStyle: {
            width: '20%',
          },
        });
      } catch {
        // User dismissed the alert
      }
    }
  };

  const productsServicesTableColumns = useGetProductsServicesTableColumns(
    channel?.id,
    onDelete,
    canManage
  );

  const handleExportToCSV = async () => {
    if (!channel) {
      return;
    }

    const data = await getBulkProductsAndServices();

    const tableKey = 'ProductsAndServicesReports';
    const fileName = `${tableKey}-${new Date().toISOString()}.csv`;

    exportCSV(data || [], csvTableFields, fileName, tableKey);
  };

  const tableData = (products.items || []) as unknown as Product[];

  const exportOptions = [
    {
      label: t('abp.productsTable.exportCsv'),
      onClick: handleExportToCSV,
    },
  ];

  return (
    <Table
      hasKeywordFilter
      columns={productsServicesTableColumns}
      data={tableData}
      isLoading={products.isLoading}
      pagination="server"
      totalRows={page?.total}
      pageSize={page.size}
      page={page.index}
      filters={tableFilters}
      onPageChange={handleSetPage}
      onPageSizeChange={handlePageSizeChange}
      onSortChange={handleSortChange}
      exportOptions={exportOptions}
      onKeywordFilterChange={handleProductNameSearch}
      onFilterChange={handleFiltersChange}
      keywordFilterTooltip={t(
        'abp.productsTable.filters.keywordFilterTooltip'
      )}
    />
  );
}
